import { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, Paper, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, LockReset } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
// import echobuddyLogo from "../../assets/echobuddy.png";
import echobuddyLogo from "../../assets/newEchoLogo.png";
import { validateToken, resetPassword, verifyEmail } from '../../service/forgotpassword';

// Add CSS animation for pulse effect
const pulseAnimation = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;

function VerifyEmail() {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [searchParams] = useSearchParams();
    const [isValidToken, setIsValidToken] = useState(null); // null = not checked yet
    const [loading, setLoading] = useState(true);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            alert('Passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            alert('Password must be at least 8 characters');
            return;
        }

        try {
            const response = await resetPassword({ token, new_password: newPassword });
            if (response.success) {
                setIsSubmitted(true);
            } else {
                alert(response.error || 'Failed to reset password');
            }
        } catch (err) {
            alert('Something went wrong. Please try again.');
        }
    };

    useEffect(() => {
        if (!token) {
            setIsValidToken(false);
            setLoading(false);
            return;
        }

        const verify = async () => {
            const response = await verifyEmail(token);

            if (response.success) {
                setIsValidToken(true);
            } else {
                setIsValidToken(false);
            }

            setLoading(false);
        };

        verify();
    }, [token]);

    if (isSubmitted) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
                p: 2,
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background animated circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 400,
                            height: 400,
                            borderRadius: '50%',
                            top: -100,
                            right: -100,
                            background: 'linear-gradient(135deg, #2ECC71, #1ABC9C)',
                            opacity: 0.1,
                            animation: 'float 20s infinite ease-in-out',
                            animationDelay: '0s',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 300,
                            height: 300,
                            borderRadius: '50%',
                            bottom: -50,
                            left: -50,
                            background: 'linear-gradient(135deg, #1ABC9C, #9B59B6)',
                            opacity: 0.1,
                            animation: 'float 20s infinite ease-in-out',
                            animationDelay: '-5s',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            top: '50%',
                            left: '10%',
                            background: 'linear-gradient(135deg, #9B59B6, #A855F7)',
                            opacity: 0.1,
                            animation: 'float 20s infinite ease-in-out',
                            animationDelay: '-10s',
                        }}
                    />
                </Box>

                <Box sx={{
                    position: 'absolute',
                    top: { xs: 18, sm: 18, md: 24 },
                    left: 24,
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75
                    }}>
                        <img
                            src="/diginxthlt-logo.svg"
                            alt="Diginxthlt Logo"
                            style={{
                                height: 45,
                                width: 45,
                                objectFit: 'cover',
                                marginTop: "0.1rem"
                            }}
                        />
                        <Box>
                            <Typography sx={{
                                fontSize: '1.4rem',
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                lineHeight: 1.1,
                                mb: 0.2
                            }}>
                                echoShine
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    letterSpacing: '0.3px',
                                    background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    display: 'block',
                                    lineHeight: 1.2
                                }}
                            >
                                Connect. Heal. Rise.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Paper
                    elevation={10}
                    sx={{
                        width: '100%',
                        maxWidth: 500,
                        borderRadius: 4,
                        overflow: 'hidden',
                        position: 'relative',
                        zIndex: 1,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                        border: '1px solid rgba(255, 255, 255, 0.18)'
                    }}
                >
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Box sx={{
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            background: 'rgba(102, 187, 106, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            color: '#4caf50',
                            fontSize: '3rem',
                            lineHeight: 1
                        }}>
                            ✓
                        </Box>
                        <Typography variant="h4" component="h1" sx={{
                            fontWeight: 700,
                            mb: 2,
                            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}>
                            Password Reset Successfully!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '90%', mx: 'auto' }}>
                            Your password has been updated. You can now login with your new password.
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={() => navigate('/')}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                boxShadow: '0 8px 20px rgba(34,197,94,0.35)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 24px rgba(34,197,94,0.5)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            Go to Login
                        </Button>
                    </Box>
                </Paper>

                {/* Float keyframes */}
                <Box
                    component="style"
                    children={`
                        @keyframes float {
                            0%,100% { transform: translate(0,0) scale(1); }
                            25% { transform: translate(20px,-20px) scale(1.05); }
                            50% { transform: translate(-10px,20px) scale(0.95); }
                            75% { transform: translate(-20px,-10px) scale(1.02); }
                        }
                    `}
                />
            </Box>
        );
    }

    return (
        <>
            <style>{pulseAnimation}</style>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
                    p: 2,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Background animated circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 400,
                            height: 400,
                            borderRadius: '50%',
                            top: -100,
                            right: -100,
                            background: 'linear-gradient(135deg, #2ECC71, #1ABC9C)',
                            opacity: 0.1,
                            animation: 'float 20s infinite ease-in-out',
                            animationDelay: '0s',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 300,
                            height: 300,
                            borderRadius: '50%',
                            bottom: -50,
                            left: -50,
                            background: 'linear-gradient(135deg, #1ABC9C, #9B59B6)',
                            opacity: 0.1,
                            animation: 'float 20s infinite ease-in-out',
                            animationDelay: '-5s',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            top: '50%',
                            left: '10%',
                            background: 'linear-gradient(135deg, #9B59B6, #A855F7)',
                            opacity: 0.1,
                            animation: 'float 20s infinite ease-in-out',
                            animationDelay: '-10s',
                        }}
                    />
                </Box>

                <Box sx={{
                    position: 'absolute',
                    top: { xs: 18, sm: 18, md: 24 },
                    left: 24,
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75
                    }}>
                        <img
                            src="/diginxthlt-logo.svg"
                            alt="Diginxthlt Logo"
                            style={{
                                height: 45,
                                width: 45,
                                objectFit: 'cover',
                                marginTop: "0.1rem"
                            }}
                        />
                        <Box>
                            <Typography sx={{
                                fontSize: '1.4rem',
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                lineHeight: 1.1,
                                mb: 0.2
                            }}>
                                echoShine
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    letterSpacing: '0.3px',
                                    background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    display: 'block',
                                    lineHeight: 1.2
                                }}
                            >
                                Connect. Heal. Rise.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Paper
                    elevation={10}
                    sx={{
                        width: '100%',
                        maxWidth: 450,
                        borderRadius: 4,
                        backdropFilter: 'blur(12px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 8px 32px rgba(31, 38, 135, 0.25)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                >
                    <Box sx={{ p: 3, textAlign: 'center' }}>

                        {loading ? (
                            <Box>
                                <CircularProgress size={60} sx={{ mb: 3 }} />
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Verifying your email...
                                </Typography>
                            </Box>
                        ) : isValidToken ? (
                            // ✅ TOKEN VALID - SHOW PASSWORD RESET FORM
                            <Box>
                                <LockReset
                                    sx={{
                                        fontSize: 60,
                                        color: '#22c55e',
                                        mb: 1.5,
                                    }}
                                />

                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 2,
                                        background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    Your Email Verification is successful
                                </Typography>

                            </Box>
                        ) : (
                            // ❌ FAILURE
                            <Box>
                                <CheckCircle
                                    sx={{
                                        fontSize: 90,
                                        color: '#ef4444',
                                        mb: 3,
                                        transform: 'rotate(45deg)'
                                    }}
                                />

                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 2,
                                        color: '#ef4444'
                                    }}
                                >
                                    Verification Failed
                                </Typography>

                                <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                                    The verification link is invalid or has expired.
                                    Please request a new verification email.
                                </Typography>

                            </Box>
                        )}

                    </Box>
                </Paper>

                {/* Float keyframes */}
                <Box
                    component="style"
                    children={`
                        @keyframes float {
                            0%,100% { transform: translate(0,0) scale(1); }
                            25% { transform: translate(20px,-20px) scale(1.05); }
                            50% { transform: translate(-10px,20px) scale(0.95); }
                            75% { transform: translate(-20px,-10px) scale(1.02); }
                        }
                    `}
                />
            </Box>
        </>
    );
}

export default VerifyEmail;
